import { Image, Label } from "@mcleod/components";
import { LogManager, ModelRow } from "@mcleod/core";
import { ImageName } from "@mcleod/images";

const DETENTION_FREE_TIME_STARTED = "F";
const DETENTION_CHARGES_STARTED = "B";
const DETENTION_DEPARTED_FROM_STOP = "D";
const DETENTION_CLOSED = "C";
const DETENTION_VISIBLE = [DETENTION_CLOSED, DETENTION_FREE_TIME_STARTED, DETENTION_CHARGES_STARTED, DETENTION_DEPARTED_FROM_STOP];

const log = LogManager.getLogger("lme.dispatch.DetentionUtil");

export class DetentionUtil {

    /**
     * Check a single stop detention status and update the corresponding image.
     * @param modelRow Row to get status from.
     * @param field Field name in the ModelRow that contains the status.
     * @param image Image to alter.
     * @param colorOverride (Optional) Color to override the default icon color.
     */
    public static checkStopDetention(modelRow: ModelRow, field: string, image: Image, colorOverride?: string) {
        if (image == null) return;
        log.debug("Checking detention status for field:", field);
        const status = modelRow.getString(field, null);
        image.visible = DETENTION_VISIBLE.includes(status);
        this.configureIconByDetentionStatus(status, image, colorOverride);
    }

    /**
     * Check multiple stops detention statuses and update the corresponding images.
     * @param modelRow Row to get status from.
     * @param statusesField Field name in the ModelRow that contains the statuses.
     * @param images A tuple containing the images for active (0), review (1), and closed (2) statuses.
     * @param colorOverride (Optional) Color to override the default icon color.
     * @private
     */
    public static checkMultipleStopsDetention(modelRow: ModelRow, statusesField: string, images: [Image, Image, Image], colorOverride?: string) {
        if (images == null) return;
        const statuses = modelRow.get(statusesField, []) as String[];
        log.debug("Checking multiple stops detention statuses for field:", statusesField);

        if (images[0]) {
            const isFreeTime = statuses.includes(DETENTION_FREE_TIME_STARTED);
            const isCharging = statuses.includes(DETENTION_CHARGES_STARTED);
            images[0].visible = isFreeTime || isCharging;
            this.configureIconByDetentionStatus(isCharging ? DETENTION_CHARGES_STARTED : DETENTION_FREE_TIME_STARTED, images[0], colorOverride);
        }

        if (images[1]) {
            images[1].visible = statuses.includes(DETENTION_DEPARTED_FROM_STOP);
            this.configureIconByDetentionStatus(DETENTION_DEPARTED_FROM_STOP, images[1], colorOverride);
        }

        if (images[2]) {
            images[2].visible = statuses.includes(DETENTION_CLOSED);
            this.configureIconByDetentionStatus(DETENTION_CLOSED, images[2], colorOverride);
        }
    }

    /**
     * Alter detention image based on the detention status.
     * @param status Detention status code.
     * @param imageOrLabel Image or Label to alter.
     * @param colorOverride (Optional) Color to override the default icon color.
     */
    public static configureIconByDetentionStatus(status: string, imageOrLabel: Image | Label, colorOverride?: string) {
        let imageName = ImageName.detention;
        let imageColor = colorOverride || "default";
        switch (status) {
            case DETENTION_FREE_TIME_STARTED:
                imageName = ImageName.detentionFree;
                imageColor = colorOverride || "caution";
                imageOrLabel.tooltip = "Free Time Started";
                break;
            case DETENTION_CHARGES_STARTED:
                imageColor = colorOverride || "error";
                imageName = ImageName.detentionCharging;
                imageOrLabel.tooltip = "Detention Charging";
                break;
            case DETENTION_CLOSED:
                imageColor = colorOverride || "subtle";
                imageName = ImageName.detentionClosed;
                imageOrLabel.tooltip = "Detention Closed";
                break;
            case DETENTION_DEPARTED_FROM_STOP:
                imageColor = colorOverride || "success";
                imageName = ImageName.detentionOccurred;
                imageOrLabel.tooltip = "Detention Occurred";
                break;
        }
        if (imageOrLabel instanceof Image) {
            imageOrLabel.name = imageName;
            imageOrLabel.color = imageColor;
        } else {
            imageOrLabel.imageName = imageName;
            imageOrLabel.imageColor = imageColor;
        }

    }
}
